import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { InteractionStatus } from '@azure/msal-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { OAuthService } from 'angular-oauth2-oidc';

import { Store } from '@ngxs/store';
import { SetSelfProfile, SetTwoFactorEnabled } from '@et/guards';
import { SelfProfile } from '@et/typings';
import { ProfileV2ApiService } from '@et/api';
import { environment } from '@et/environment';
import { AuthService } from '@et/auth';

interface BetterAuthIdTokenClaims {
  sub?: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  roles?: unknown;
}

@Component({
  selector: 'admin-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private msalBroadcastService = inject(MsalBroadcastService);
  private msal = inject(MsalService);
  private store = inject(Store);
  private profileV2Api = inject(ProfileV2ApiService);
  private router = inject(Router);
  private oauth = inject(OAuthService, { optional: true });
  private authService = inject(AuthService);

  title = 'admin';
  private readonly _onDestroy$ = new Subject<void>();

  ngOnInit() {
    if (environment.useBetterAuth) {
      this.bootstrapBetterAuth();
    } else {
      this.bootstrapMsal();
    }
  }

  /**
   * Mirrors apps/escrowtab's bootstrapBetterAuth, trimmed to just the self-profile load —
   * the admin SPA has no company-scoped state or business-role permissions to prime. Loading
   * `selfProfile` is what lets `etAdminGuard` (see @et/guards) read `isEtAdmin` on the
   * subsequent `/app` navigation.
   */
  private bootstrapBetterAuth() {
    if (!this.oauth?.hasValidAccessToken() && !this.oauth?.hasValidIdToken()) {
      return;
    }
    this.profileV2Api.getSelfProfile().subscribe({
      next: (self) => {
        this.dispatchProfileLoaded(self, this.idTokenEmail());
        this.loadTwoFactorEnabledForBetterAuth();
      },
      error: (err) => {
        const email = this.idTokenEmail();
        if (email) {
          Sentry.setUser({ email });
        }
        Sentry.captureException(err, { tags: { area: 'oidc-bootstrap' } });
        this.oauth?.logOut(true);
        void this.router.navigateByUrl('/auth/login?error=profile_load');
      },
    });
  }

  /**
   * Reads Better Auth's own live `user.twoFactorEnabled` (distinct from
   * `SelfProfile.requiredMfaMethod`, Profile v2's policy) right after the profile
   * loads, so `mfaRequiredGuard` (@et/guards) can decide whether to block access
   * behind the mandatory Authenticator-app enrollment gate.
   */
  private loadTwoFactorEnabledForBetterAuth(): void {
    this.authService.getSession().subscribe((session) => {
      this.store.dispatch(
        new SetTwoFactorEnabled(session?.twoFactorEnabled ?? false),
      );
    });
  }

  /** Legacy MSAL bootstrap, mirroring apps/escrowtab minus the company/permissions bits. */
  private bootstrapMsal() {
    this.msal.handleRedirectObservable().subscribe();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._onDestroy$),
      )
      .subscribe(() => {
        const account = this.msal.instance.getActiveAccount();
        if (!account) {
          return;
        }

        this.profileV2Api.getSelfProfile().subscribe({
          next: (self) => {
            this.dispatchProfileLoaded(self, account.username);
            Sentry.setUser({ email: account.username });
          },
          error: () => {
            Sentry.setUser({ email: account.username });
          },
        });
      });
  }

  private dispatchProfileLoaded(self: SelfProfile, email: string | undefined) {
    this.store.dispatch(new SetSelfProfile(self));
    if (email) {
      Sentry.setUser({ email });
      Sentry.setContext('user_scope', {
        email,
        is_et_admin: self.isEtAdmin,
        profile_id: self.profileId,
      });
    }
  }

  /** Pull the email out of the Better Auth ID token; null when no token is loaded. */
  private idTokenEmail(): string | undefined {
    const claims = this.oauth?.getIdentityClaims() as
      | BetterAuthIdTokenClaims
      | undefined;
    return claims?.email ?? undefined;
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
